import type {
  HistoryCustomerSummary,
  HistoryHandDelivery,
  HistoryInvoice,
  HistoryInvoiceLink,
  HistoryLog,
  HistoryPlannerPreview,
  HistoryState,
  HistoryStatusChange,
  HistoryStatusTarget,
  HistoryWorklistEntry,
  Shipment
} from '@/api/types'

export const HISTORY_STATE_VALUES: HistoryState[] = ['pending', 'applied', 'checked', 'skipped', 'flagged']

export const HISTORY_STATE_LABELS: Record<HistoryState, string> = {
  pending: 'Kvar',
  applied: 'Inlagd',
  skipped: 'Hoppad över',
  flagged: 'Flaggad',
  checked: 'Kontrollerad'
}

export const HISTORY_STATE_STYLES: Record<HistoryState, string> = {
  pending: 'bg-muted text-muted-foreground border-border',
  applied: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  skipped: 'bg-amber-50 text-amber-700 border-amber-200',
  flagged: 'bg-rose-50 text-rose-700 border-rose-200',
  checked: 'bg-sky-50 text-sky-700 border-sky-200'
}

export const HISTORY_PHASE_LABELS: Record<number, string> = {
  1: 'Steg 1, slutat',
  2: 'Steg 2, kund'
}

export type RecordStateKey = 'active' | 'cancelled' | 'ended' | 'withdrawn' | 'held' | 'paused'

export const RECORD_STATE_KEYS: RecordStateKey[] = ['active', 'cancelled', 'ended', 'withdrawn', 'held', 'paused']

export const RECORD_STATE_LABELS: Record<RecordStateKey, string> = {
  active: 'Aktiv',
  cancelled: 'Uppsagd, sista leverans kvar',
  ended: 'Slutat',
  withdrawn: 'Ångrad',
  held: 'Vilande',
  paused: 'Pausad'
}

// En senare omgång öppnar kunder som redan var klara, med en ny fråga till teamet.
export function describeRound(round: number): string {
  if (round === 1) return 'Första omgången'
  if (round === 2) return 'Andra omgången'

  return `Omgång ${round}`
}

export function roundsOf(rows: HistoryCustomerSummary[]): number[] {
  return [...new Set(rows.map((row) => row.round))].sort((a, b) => a - b)
}

export function getFlagTitle(entry: HistoryWorklistEntry): string {
  const round = entry.round ?? 1

  return round > 1 ? `Att avgöra i ${describeRound(round).toLowerCase()}` : 'Fråga från avstämningen'
}

export function recordStateKeyOf(status: string, nextSequence: number | null): RecordStateKey {
  if (status === 'held') return 'held'
  if (status === 'paused') return 'paused'
  if (status === 'withdrawn') return 'withdrawn'
  if (status === 'stopped') return nextSequence === null ? 'ended' : 'cancelled'

  return 'active'
}

export function describeRecordSummary(row: HistoryCustomerSummary): string {
  const key = recordStateKeyOf(row.record_status, row.record_next_sequence)

  if (key === 'cancelled') return `Uppsagd, sista leverans ${row.record_last_delivery ?? '?'}`
  if (key === 'active') return row.record_next_sequence === null ? 'Aktiv' : `Aktiv, nästa leverans ${row.record_next_sequence}`

  return RECORD_STATE_LABELS[key]
}

export const HISTORY_STATUS_TARGET_LABELS: Record<HistoryStatusTarget, string> = {
  active: 'Aktiv igen',
  terminated: 'Avslutad',
  withdrawn: 'Fullständig ånger',
  withdrawn_keep_first: 'Ånger med leveranser',
  withdrawn_pending_return: 'Ångrad, väntar på retur',
  cancelled: 'Uppsagd, med sista leverans'
}

export const HISTORY_STATUS_TARGETS: HistoryStatusTarget[] = [
  'active',
  'terminated',
  'withdrawn',
  'withdrawn_keep_first',
  'withdrawn_pending_return',
  'cancelled'
]

export const HISTORY_NO_STATUS_CHANGE = 'none'

// Valen i rutan "Sätts till vid inläsningen". En ånger behåller leveranserna till och med ett nummer, som vid ånger i admin.
// Aktiv igen finns bara för en kund som inte är aktiv i plattformen: avslutet, uppsägningen eller ångern var fel.
export interface StatusOption {
  value: string
  label: string
  to: HistoryStatusTarget
  finalSequence: number | null
}

export const REACTIVATE_OPTION: StatusOption = {
  value: 'active',
  label: 'Aktiv igen, kunden fortsätter som vanligt',
  to: 'active',
  finalSequence: null
}

export const STATUS_OPTIONS: StatusOption[] = [
  { value: 'terminated', label: 'Avslutad', to: 'terminated', finalSequence: null },
  { value: 'withdrawn', label: 'Fullständig ånger, inget behålls', to: 'withdrawn', finalSequence: null },
  {
    value: 'withdrawn_keep_first',
    label: 'Ånger med leveranser, behåller till och med nummer',
    to: 'withdrawn_keep_first',
    finalSequence: null
  },
  { value: 'withdrawn_pending_return', label: 'Ångrad, väntar på retur', to: 'withdrawn_pending_return', finalSequence: null },
  { value: 'cancelled', label: 'Uppsagd, sista leverans nummer', to: 'cancelled', finalSequence: null }
]

export function statusOptionValue(status: HistoryStatusChange | null): string {
  return status === null ? HISTORY_NO_STATUS_CHANGE : status.to
}

// Valet visas också för en redan inlagd Aktiv igen, så att kortet kan visa vad som gjordes när kunden nu är aktiv.
export function statusOptionsFor(subscriptionStatus: string | undefined, current: HistoryStatusChange | null): StatusOption[] {
  const offerReactivate = (subscriptionStatus !== undefined && subscriptionStatus !== 'active') || current?.to === 'active'

  return offerReactivate ? [REACTIVATE_OPTION, ...STATUS_OPTIONS] : STATUS_OPTIONS
}

// Aktiv igen har inget datum: avslutets datum tas bort i stället.
export function statusTakesDate(target: HistoryStatusTarget | null | undefined): boolean {
  return target !== undefined && target !== null && target !== 'active'
}

// Lägen där teamet själva anger numret på den sista leverans kunden får.
export function statusTakesFinalSequence(target: HistoryStatusTarget | null | undefined): boolean {
  return target === 'cancelled' || target === 'withdrawn_keep_first'
}

export const HAND_DELIVERY_CONTENTS: Record<string, string> = {
  starter: 'Startpaket',
  refill: 'Påfyllning, 3 kollagen'
}

export const HISTORY_INVOICE_STATUS_LABELS: Record<HistoryInvoice['status'], string> = {
  paid: 'Betald',
  partially_paid: 'Delbetald',
  unpaid: 'Obetald',
  voided: 'Krediterad'
}

export const EXTERNAL_LINK_VALUE = 'external'
export const NEW_INVOICE_WHY = 'faktura utan känd leverans, tillagd från Historik-sidan'

export function summarizeHistoryRow(row: HistoryCustomerSummary): string {
  const parts: string[] = []

  if (row.hand_deliveries > 0) parts.push(`${row.hand_deliveries} handskickade paket`)
  if (row.invoices > 0) parts.push(`${row.invoices} fakturor`)
  if (row.cancel_labels > 0) parts.push(`${row.cancel_labels} fraktsedel tas bort`)
  if (row.status_to) parts.push(HISTORY_STATUS_TARGET_LABELS[row.status_to as HistoryStatusTarget] ?? row.status_to)
  if (row.postnord_off) parts.push('leveranserna av')
  if (row.identity_fix) parts.push('personnumret rättas')

  return parts.length > 0 ? parts.join(', ') : 'bara status'
}

export function countByState(rows: HistoryCustomerSummary[]): Record<HistoryState, number> {
  const counts: Record<HistoryState, number> = { pending: 0, applied: 0, skipped: 0, flagged: 0, checked: 0 }

  for (const row of rows) counts[row.state] += 1

  return counts
}

export function matchesHistorySearch(row: HistoryCustomerSummary, search: string): boolean {
  const needle = search.trim().toLowerCase()

  if (needle === '') return true

  return (
    String(row.customer_id) === needle ||
    row.name.toLowerCase().includes(needle) ||
    row.person.replace('-', '').includes(needle.replace('-', ''))
  )
}

export function describeRecordState(entry: HistoryWorklistEntry): string {
  const state = entry.recordState
  const next = state.next

  if (state.status === 'held') return 'Vilande: fakturan för leverans 1 hos inkasso'
  if (state.status === 'paused') return 'Pausad på kundens begäran'
  if (state.status === 'withdrawn') return state.lastDelivery === 1 ? 'Ångrad, behåller leverans 1' : 'Ångrad'
  if (state.status === 'stopped' && next === null) return `Slutat efter leverans ${state.onDelivery}`
  if (state.status === 'stopped') return `Uppsagd, sista leverans ${state.lastDelivery ?? '?'}`

  return `Aktiv, har fått ${state.onDelivery} ${state.onDelivery === 1 ? 'leverans' : 'leveranser'}`
}

export function describeNextDelivery(entry: HistoryWorklistEntry): string {
  const next = entry.recordState.next

  if (next === null) return 'Inget mer ska skickas'
  if (next.label !== null) return `Leverans ${next.sequence} är redan bokad (rad ${next.label})`

  return `Leverans ${next.sequence}`
}

export function describeLink(link: HistoryInvoiceLink): string {
  if (link.kind === 'existing') return `leverans i plattformen, rad ${link.shipmentId}`
  if (link.kind === 'new') return `handskickad leverans ${link.sequence}`
  if (link.kind === 'prepaid') return `förskott på bokad leverans ${link.sequence} (rad ${link.shipmentId})`

  return `utan leverans: ${link.why}`
}

export interface LinkOption {
  value: string
  label: string
  link: HistoryInvoiceLink
}

export function encodeLink(link: HistoryInvoiceLink): string {
  if (link.kind === 'existing') return `existing:${link.shipmentId}`
  if (link.kind === 'new') return `new:${link.sequence}`
  if (link.kind === 'prepaid') return `prepaid:${link.shipmentId}`

  return EXTERNAL_LINK_VALUE
}

const HANDED_IN_STATUSES = ['dispatched', 'delivered']
const BOOKED_STATUSES = ['booking', 'booked', 'failed']

// De länkar en faktura kan få: plattformens inlämnade leveranser, körningens handleveranser, den bokade nästa leveransen som förskott, eller ingen.
export function linkOptionsFor(entry: HistoryWorklistEntry, shipments: Shipment[], current: HistoryInvoiceLink): LinkOption[] {
  const cancelled = new Set(entry.cancelLabels.map((label) => label.id))
  const options: LinkOption[] = []

  for (const shipment of shipments) {
    if (cancelled.has(shipment.id)) continue
    if (HANDED_IN_STATUSES.includes(shipment.status))
      options.push({
        value: `existing:${shipment.id}`,
        label: `Leverans ${shipment.sequence} i plattformen (rad ${shipment.id})`,
        link: { kind: 'existing', shipmentId: shipment.id }
      })
    if (BOOKED_STATUSES.includes(shipment.status) && entry.recordState.next?.label === shipment.id)
      options.push({
        value: `prepaid:${shipment.id}`,
        label: `Förskott på bokad leverans ${shipment.sequence} (rad ${shipment.id})`,
        link: { kind: 'prepaid', shipmentId: shipment.id, sequence: shipment.sequence }
      })
  }
  for (const delivery of entry.handDeliveries)
    options.push({
      value: `new:${delivery.sequence}`,
      label: `Handskickad leverans ${delivery.sequence} (${delivery.sentOn})`,
      link: { kind: 'new', sequence: delivery.sequence }
    })

  const why = current.kind === 'external' ? current.why : NEW_INVOICE_WHY

  options.push({ value: EXTERNAL_LINK_VALUE, label: 'Utan leverans', link: { kind: 'external', why } })

  return options
}

/* ---------- ändringar på posten, alla rena ---------- */

const REMOVABLE_LABEL_STATUSES: Shipment['status'][] = ['booking', 'booked', 'failed']
export const TEAM_LABEL_REASON = 'tas bort av teamet från Historik-sidan'

// En bokad fraktsedel teamet kan ta bort. Är den avstämningens bokade nästa leverans väntar avstämningen sig i stället
// att leveransen bokas på nytt, så att motorns kontroll stämmer; Behåll fraktsedeln ställer tillbaka det.
export function canMarkLabelForRemoval(entry: HistoryWorklistEntry, shipment: Shipment): boolean {
  const isListed = entry.cancelLabels.some((label) => label.id === shipment.id)

  return REMOVABLE_LABEL_STATUSES.includes(shipment.status) && !isListed
}

export function addCancelLabel(entry: HistoryWorklistEntry, shipment: Shipment): HistoryWorklistEntry {
  const label = { id: shipment.id, sequence: shipment.sequence, postnordId: shipment.postnord_shipment_id, reason: TEAM_LABEL_REASON }
  const next = entry.recordState.next
  const isRecordNext = next !== null && next.label === shipment.id
  const recordState = isRecordNext ? { ...entry.recordState, next: { ...next, label: null } } : entry.recordState

  return { ...entry, recordState, cancelLabels: [...entry.cancelLabels, label] }
}

export function removeCancelLabel(entry: HistoryWorklistEntry, id: number): HistoryWorklistEntry {
  const removed = entry.cancelLabels.find((label) => label.id === id)
  const next = entry.recordState.next
  const wasRecordNext = removed?.reason === TEAM_LABEL_REASON && next !== null && next.label === null && next.sequence === removed.sequence
  const recordState = wasRecordNext ? { ...entry.recordState, next: { ...next, label: id } } : entry.recordState

  return { ...entry, recordState, cancelLabels: entry.cancelLabels.filter((label) => label.id !== id) }
}

export function removeRenumber(entry: HistoryWorklistEntry, id: number): HistoryWorklistEntry {
  return { ...entry, renumber: entry.renumber.filter((row) => row.id !== id) }
}

export function updateHandDelivery(entry: HistoryWorklistEntry, index: number, patch: Partial<HistoryHandDelivery>): HistoryWorklistEntry {
  const handDeliveries = entry.handDeliveries.map((delivery, position) => (position === index ? { ...delivery, ...patch } : delivery))

  return { ...entry, handDeliveries }
}

export function removeHandDelivery(entry: HistoryWorklistEntry, index: number): HistoryWorklistEntry {
  return { ...entry, handDeliveries: entry.handDeliveries.filter((_, position) => position !== index) }
}

export function nextFreeSequence(entry: HistoryWorklistEntry, shipments: Shipment[]): number {
  const cancelled = new Set(entry.cancelLabels.map((label) => label.id))
  const renumbered = new Map(entry.renumber.map((row) => [row.id, row.to]))
  const taken = new Set<number>()

  for (const shipment of shipments) {
    if (cancelled.has(shipment.id) || shipment.status === 'cancelled' || shipment.status === 'returned') continue
    taken.add(renumbered.get(shipment.id) ?? shipment.sequence)
  }
  for (const delivery of entry.handDeliveries) taken.add(delivery.sequence)

  let sequence = 1

  while (taken.has(sequence)) sequence += 1

  return sequence
}

export function addHandDelivery(entry: HistoryWorklistEntry, sequence: number, today: string): HistoryWorklistEntry {
  const contents = sequence === 1 ? 'starter' : 'refill'

  return {
    ...entry,
    handDeliveries: [...entry.handDeliveries, { sequence, sentOn: today, contents }].sort((a, b) => a.sequence - b.sequence)
  }
}

export function removeInvoice(entry: HistoryWorklistEntry, index: number): HistoryWorklistEntry {
  return { ...entry, invoices: entry.invoices.filter((_, position) => position !== index) }
}

// En faktura teamet lägger till med bara numret: servern hämtar resten från Capway vid förhandsgranskningen.
export function addInvoiceByNumber(entry: HistoryWorklistEntry, number: string, today: string): HistoryWorklistEntry {
  const invoice: HistoryInvoice = {
    number: number.trim(),
    guid: '',
    reference: 'okänd',
    date: today,
    dueOn: today,
    amount: 0,
    paid: 0,
    remaining: 0,
    status: 'unpaid',
    link: { kind: 'external', why: NEW_INVOICE_WHY },
    lines: null
  }

  return { ...entry, invoices: [...entry.invoices, invoice] }
}

export function setInvoiceLink(entry: HistoryWorklistEntry, index: number, link: HistoryInvoiceLink): HistoryWorklistEntry {
  return { ...entry, invoices: entry.invoices.map((invoice, position) => (position === index ? { ...invoice, link } : invoice)) }
}

export function setExternalWhy(entry: HistoryWorklistEntry, index: number, why: string): HistoryWorklistEntry {
  return setInvoiceLink(entry, index, { kind: 'external', why })
}

export function statusChangeFor(target: HistoryStatusTarget, since: string | null, finalSequence: number | null): HistoryStatusChange {
  const dateColumn = target.startsWith('withdrawn') ? 'withdrawn_at' : 'cancelled_at'

  if (target === 'active') return { to: target, dateColumn, since: null, finalSequence: null }
  if (target === 'cancelled') return { to: target, dateColumn, since, finalSequence }
  if (target === 'withdrawn_keep_first')
    return { to: target, dateColumn, since, finalSequence: finalSequence === null || finalSequence <= 1 ? null : finalSequence }

  return { to: target, dateColumn, since, finalSequence: null }
}

export function setStatusChange(entry: HistoryWorklistEntry, status: HistoryStatusChange | null): HistoryWorklistEntry {
  return { ...entry, status }
}

export function setPostnordOff(entry: HistoryWorklistEntry, why: string | null): HistoryWorklistEntry {
  return { ...entry, postnordOff: why === null ? null : { why } }
}

export function setPause(entry: HistoryWorklistEntry, why: string | null): HistoryWorklistEntry {
  return { ...entry, pause: why === null ? null : { why } }
}

export function isPauseChosen(entry: HistoryWorklistEntry): boolean {
  return entry.pause !== undefined && entry.pause !== null
}

export function removeIdentityFix(entry: HistoryWorklistEntry): HistoryWorklistEntry {
  return { ...entry, identityFix: null }
}

// Något att skriva: en rad som läggs till, tas bort eller ändras, eller ett läge eller en flagga som sätts. Då är Kontrollerad fel knapp.
export function hasAnythingToApply(entry: HistoryWorklistEntry): boolean {
  return (
    entry.cancelLabels.length > 0 ||
    entry.renumber.length > 0 ||
    entry.handDeliveries.length > 0 ||
    entry.invoices.length > 0 ||
    entry.status !== null ||
    entry.postnordOff !== null ||
    isPauseChosen(entry) ||
    entry.identityFix !== null ||
    entry.billThroughPlatform.length > 0
  )
}

export function isSameEntry(a: HistoryWorklistEntry | null, b: HistoryWorklistEntry | null): boolean {
  return JSON.stringify(a) === JSON.stringify(b)
}

export function describePlanner(preview: HistoryPlannerPreview | null): string {
  if (preview === null) return 'Okänt'
  if (preview.fits) return `Motorn skulle härnäst ge: ${preview.planned}.`

  return `Motorn skulle ge ${preview.planned}, avstämningen väntar ${preview.expected}.`
}

const RAW_STATUS_LABELS: Record<string, string> = {
  active: 'Aktiv',
  cancelled: 'Uppsagd',
  withdrawn: 'Ångrad',
  withdrawn_pending_return: 'Ångrad, väntar på retur',
  withdrawn_keep_first: 'Ångrad, behåller leveranser',
  terminated: 'Avslutad'
}

const NOTHING_TO_APPLY = 'Inget nytt läggs till'
const NOTHING_APPLIED = 'Inget nytt lades till, kunden var redan rätt i plattformen'

function rawStatusLabel(status: string): string {
  return RAW_STATUS_LABELS[status] ?? status
}

function describeStatusChange(status: HistoryStatusChange): string {
  const label = HISTORY_STATUS_TARGET_LABELS[status.to]

  if (status.to === 'active') return 'läget sätts till Aktiv igen, avslutets datum och sista leverans tas bort'
  if (status.to === 'cancelled') return `läget sätts till Uppsagd, sista leverans ${status.finalSequence ?? '?'}`
  if (status.to === 'withdrawn_keep_first') return `läget sätts till ${label}, behåller till och med ${status.finalSequence ?? 1}`

  return `läget sätts till ${label}`
}

function countParts(handDeliveries: number, invoices: number, cancelledLabels: number, past: boolean): string[] {
  const parts: string[] = []
  const removed = past ? 'borttagna' : 'tas bort'

  if (handDeliveries > 0) parts.push(`${handDeliveries} handskickade paket`)
  if (invoices > 0) parts.push(`${invoices} fakturor`)
  if (cancelledLabels > 0) parts.push(`${cancelledLabels} fraktsedlar ${removed}`)

  return parts
}

export function getApplyConfirmMessage(entry: HistoryWorklistEntry, enablePostnord: boolean, waitingForPostnord: boolean): string {
  const parts = countParts(entry.handDeliveries.length, entry.invoices.length, entry.cancelLabels.length, false)

  if (entry.renumber.length > 0) parts.push(`${entry.renumber.length} leveranser numreras om`)
  if (entry.status) parts.push(describeStatusChange(entry.status))
  if (entry.postnordOff) parts.push('leveranserna sätts av')
  else if (enablePostnord && waitingForPostnord) parts.push('leveranserna slås på')
  if (isPauseChosen(entry)) parts.push('abonnemanget pausas')

  const summary = parts.length > 0 ? parts.join(', ') : NOTHING_TO_APPLY

  return `Lägg in historiken för ${entry.name} (kund nr ${entry.prodCustomerId})? ${summary}. Det skrivs i plattformen direkt.`
}

export function describeAppliedLog(log: HistoryLog): string {
  const before = log.subscriptionBefore
  const after = log.subscriptionAfter
  const parts = countParts(log.shipments.length, log.invoices.length, log.cancelledShipmentIds.length, true)

  if (log.renumbered.length > 0) parts.push(`${log.renumbered.length} leveranser omnumrerade`)
  if (before.status !== after.status) parts.push(`läget ${rawStatusLabel(before.status)} → ${rawStatusLabel(after.status)}`)
  if (before.final_sequence !== after.final_sequence && after.final_sequence !== null) parts.push(`sista leverans ${after.final_sequence}`)
  if (!before.enable_postnord && after.enable_postnord) parts.push('leveranserna påslagna')
  if (before.enable_postnord && !after.enable_postnord) parts.push('leveranserna avstängda')
  if (!before.enable_capway && after.enable_capway) parts.push('faktureringen påslagen')
  if (!before.paused_at && after.paused_at) parts.push('abonnemanget pausat')
  if (log.identityBefore !== log.identityAfter) parts.push('personnumret rättat')

  return parts.length > 0 ? `${parts.join(', ')}.` : `${NOTHING_APPLIED}.`
}

export const REVERT_CONFIRM_MESSAGE = 'Ångra inläggningen? Allt som lades in för kunden tas bort och kunden står som kvar igen.'
